import { maxBy, minBy } from 'lodash';
import { LngLatBoundsLike } from 'mapbox-gl';
import { MutableRefObject } from 'react';
import { MapRef } from 'react-map-gl';
import { MAPBOX_CONFIG } from '~/constants/mapbox';
import { LocationIQReverseLocation, MapHome } from '@habity/types';
import { MarkerType } from '@habity/types/map';
import * as turf from '@turf/turf';
import { Feature, FeatureCollection } from '@turf/turf';

export const getMinOrMax = (
  markers: MarkerType[],
  minOrMax: 'max' | 'min',
  latOrLng: 'latitude' | 'longitude'
) => {
  if (minOrMax === 'max') {
    return (maxBy(markers, (value) => value[latOrLng]) as TODO)[latOrLng];
  } else {
    return (minBy(markers, (value) => value[latOrLng]) as TODO)[latOrLng];
  }
};

const { INITIAL_VIEW_STATE, DEFAULT_ZOOM_DURATION } = MAPBOX_CONFIG;

export const getMarkerBounds = (markers: MarkerType[]) => {
  const maxLat = getMinOrMax(markers, 'max', 'latitude');
  const minLat = getMinOrMax(markers, 'min', 'latitude');
  const maxLng = getMinOrMax(markers, 'max', 'longitude');
  const minLng = getMinOrMax(markers, 'min', 'longitude');

  const southWest = [minLng, minLat];
  const northEast = [maxLng, maxLat];
  return [southWest, northEast] as LngLatBoundsLike;
};

export const getLocationBounds = (location: LocationIQReverseLocation) => {
  const { boundingbox } = location;

  return [
    [boundingbox[2], boundingbox[0]],
    [boundingbox[3], boundingbox[1]],
  ] as LngLatBoundsLike;
};

export const resetMapBounds = (
  mapRef: MutableRefObject<MapRef | null>,
  duration: number = DEFAULT_ZOOM_DURATION
) => {
  return mapRef?.current?.easeTo({
    center: [INITIAL_VIEW_STATE.longitude, INITIAL_VIEW_STATE.latitude],
    zoom: INITIAL_VIEW_STATE.zoom,
    duration,
  });
};

const getFeatureCollection = (polygons: TODO[] | null) => {
  const features = polygons?.map((geometry: TODO) => {
    return {
      type: 'Feature',
      properties: {},
      geometry: geometry,
    };
  });

  const geojson = {
    type: 'FeatureCollection',
    features: features,
  };
  return geojson as FeatureCollection;
};

export const fitPolygonBounds = (
  mapRef: MutableRefObject<MapRef | null>,
  polygons: TODO[] | null,
  padding: number,
  duration: number = DEFAULT_ZOOM_DURATION
) => {
  if (!polygons || polygons.length === 0 || !mapRef || !mapRef.current)
    return resetMapBounds(mapRef, duration);

  const bbox = turf.bbox(getFeatureCollection(polygons));

  if (!bbox) return resetMapBounds(mapRef, duration);

  return mapRef?.current?.fitBounds(bbox as TODO, {
    padding,
    duration,
  });
};

export const fitPointsBounds = (
  mapRef: MutableRefObject<MapRef | null>,
  points: GeoJSON.Point[],
  padding = 50,
  duration: number = DEFAULT_ZOOM_DURATION
) => {
  if (!points || points.length === 0 || !mapRef || !mapRef.current) return;

  const featureCollection: FeatureCollection = {
    type: 'FeatureCollection',
    features: points.map((point) => ({
      type: 'Feature',
      properties: {},
      geometry: point,
    })),
  };

  const bbox = turf.bbox(featureCollection);

  if (!bbox) return;

  return mapRef?.current?.fitBounds(bbox as TODO, {
    padding,
    duration,
  });
};

export const getIntialViewState = (polygons: TODO[] | null) => {
  if (!polygons || polygons.length === 0) return INITIAL_VIEW_STATE;
  const center = turf.center(getFeatureCollection(polygons));
  const { geometry } = center as Feature;
  const { coordinates } = geometry as TODO;
  return {
    longitude: coordinates[0],
    latitude: coordinates[1],
    zoom: INITIAL_VIEW_STATE.zoom,
  };
};

export const parseGeometry = (
  geometry: string | TODO | null | undefined
): GeoJSON.Geometry | null => {
  if (!geometry) {
    return null;
  }

  try {
    return typeof geometry === 'string'
      ? (JSON.parse(geometry) as TODO)
      : geometry;
  } catch {
    console.error('Invalid geometry format');
    return null;
  }
};

export const extractPolygonsFromGeometries = (
  geometries: GeoJSON.Geometry[]
): GeoJSON.Polygon[] => {
  return geometries.reduce((acc: GeoJSON.Polygon[], geometry) => {
    const parsedGeometry = parseGeometry(geometry);

    if (!parsedGeometry) return acc;

    if (parsedGeometry.type === 'MultiPolygon') {
      parsedGeometry.coordinates.forEach((polygonCoordinates) => {
        acc.push({
          type: 'Polygon',
          coordinates: polygonCoordinates,
        });
      });
    }

    if (parsedGeometry.type === 'Polygon') {
      acc.push(parsedGeometry);
    }

    return acc;
  }, []);
};

export const extractHomePoints = (
  homes: Array<MapHome | undefined>
): GeoJSON.Point[] => {
  return homes
    ?.filter((home) => home !== undefined)
    ?.reduce((acc: GeoJSON.Point[], home) => {
      const { location } = home?.address || {};
      const geometry = parseGeometry(location);
      if (geometry?.type === 'Point') {
        acc.push({
          type: 'Point',
          coordinates: geometry.coordinates,
        } as GeoJSON.Point);
      }
      return acc;
    }, []);
};

export type MapGeometries = {
  points: GeoJSON.Point[];
  areaPolygons: GeoJSON.Polygon[];
  municipalityPolygons: GeoJSON.Polygon[];
};
